/**
 * Service de gestion des permissions côté frontend
 */
import apiService from './api';

export interface Permission {
  id: number;
  code: string;
  name: string;
  description: string;
  category: string;
  access_level: 'READ' | 'WRITE' | 'DELETE' | 'ADMIN';
  is_active: boolean;
}

export interface Role {
  id: number;
  name: string;
  display_name: string;
  description: string;
  is_active: boolean;
  permissions_count: number;
  users_count: number;
}

export interface UserPermission {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  permissions: Permission[];
  roles: Role[];
  permission_codes: string[];
}

class PermissionService {
  private permissions: Permission[] = [];
  private roles: Role[] = [];
  private permissionCodes: string[] = [];

  /**
   * Initialise les permissions de l'utilisateur connecté
   */
  async initializeUserPermissions(): Promise<void> {
    try {
      const response = await apiService.get('/permissions/my-permissions/');
      this.permissionCodes = response.data.permission_codes || [];
      
      // Mettre en cache les permissions et rôles
      this.permissions = response.data.permissions || [];
      this.roles = response.data.roles || [];
    } catch (error) {
      this.permissionCodes = [];
      this.permissions = [];
      this.roles = [];
    }
  }

  /**
   * Vérifie si l'utilisateur a une permission spécifique
   */
  hasPermission(permissionCode: string): boolean {
    return this.permissionCodes.includes(permissionCode);
  }

  /**
   * Vérifie si l'utilisateur a au moins une des permissions spécifiées
   */
  hasAnyPermission(permissionCodes: string[]): boolean {
    return permissionCodes.some(code => this.hasPermission(code));
  }

  /**
   * Vérifie si l'utilisateur a toutes les permissions spécifiées
   */
  hasAllPermissions(permissionCodes: string[]): boolean {
    return permissionCodes.every(code => this.hasPermission(code));
  }

  /**
   * Vérifie si l'utilisateur a un rôle spécifique
   */
  hasRole(roleName: string): boolean {
    return this.roles.some(role => role.name === roleName);
  }

  /**
   * Vérifie si l'utilisateur a au moins un des rôles spécifiés
   */
  hasAnyRole(roleNames: string[]): boolean {
    return roleNames.some(roleName => this.hasRole(roleName));
  }

  /**
   * Récupère toutes les permissions de l'utilisateur
   */
  getUserPermissions(): Permission[] {
    return this.permissions;
  }

  /**
   * Récupère tous les rôles de l'utilisateur (synchronisé)
   */
  getCurrentUserRoles(): Role[] {
    return this.roles;
  }

  /**
   * Récupère les codes des permissions de l'utilisateur
   */
  getUserPermissionCodes(): string[] {
    return this.permissionCodes;
  }

  /**
   * Récupère les permissions groupées par catégorie
   */
  getPermissionsByCategory(): Record<string, Permission[]> {
    const categories: Record<string, Permission[]> = {};
    
    this.permissions.forEach(permission => {
      if (!categories[permission.category]) {
        categories[permission.category] = [];
      }
      categories[permission.category].push(permission);
    });
    
    return categories;
  }

  /**
   * Vérifie les permissions pour une action spécifique
   */
  canPerformAction(action: string, resource: string): boolean {
    const permissionCode = `${resource}.${action}`;
    return this.hasPermission(permissionCode);
  }

  /**
   * Récupère toutes les permissions disponibles (pour l'admin)
   */
  async getAllPermissions(): Promise<Permission[]> {
    try {
      const response = await apiService.get('/permissions/permissions/');
      return response.data.results || response.data;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des permissions:', error);
      return [];
    }
  }

  /**
   * Récupère tous les rôles disponibles (pour l'admin)
   */
  async getAllRoles(): Promise<Role[]> {
    try {
      const response = await apiService.get('/permissions/roles/');
      return response.data.results || response.data;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des rôles:', error);
      return [];
    }
  }

  /**
   * Récupère les permissions d'un rôle spécifique
   */
  async getRolePermissions(roleId: number): Promise<Permission[]> {
    try {
      const response = await apiService.get(`/permissions/roles/${roleId}/permissions/`);
      return response.data;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des permissions du rôle:', error);
      return [];
    }
  }

  /**
   * Assigne une permission à un rôle
   */
  async assignPermissionToRole(roleId: number, permissionId: number, accessLevel: string = 'READ'): Promise<boolean> {
    try {
      await apiService.post(`/permissions/roles/${roleId}/assign_permission/`, {
        permission_id: permissionId,
        access_level: accessLevel
      });
      return true;
    } catch (error) {
      console.error('❌ Erreur lors de l\'assignation de la permission:', error);
      return false;
    }
  }

  /**
   * Retire une permission d'un rôle
   */
  async removePermissionFromRole(roleId: number, permissionId: number): Promise<boolean> {
    try {
      await apiService.delete(`/permissions/roles/${roleId}/remove_permission/`, {
        data: { permission_id: permissionId }
      });
      return true;
    } catch (error) {
      console.error('❌ Erreur lors de la suppression de la permission:', error);
      return false;
    }
  }

  /**
   * Assigne un rôle à un utilisateur
   */
  async assignRoleToUser(userId: number, roleId: number, expiresAt?: string): Promise<boolean> {
    try {
      await apiService.post('/permissions/user-roles/assign_role/', {
        user_id: userId,
        role_id: roleId,
        expires_at: expiresAt
      });
      return true;
    } catch (error) {
      console.error('❌ Erreur lors de l\'assignation du rôle:', error);
      return false;
    }
  }

  /**
   * Retire un rôle d'un utilisateur
   */
  async removeRoleFromUser(userRoleId: number): Promise<boolean> {
    try {
      await apiService.delete(`/permissions/user-roles/${userRoleId}/remove_role/`);
      return true;
    } catch (error) {
      console.error('❌ Erreur lors de la suppression du rôle:', error);
      return false;
    }
  }

  /**
   * Récupère les rôles d'un utilisateur
   */
  async getUserRoles(userId: number): Promise<Role[]> {
    try {
      const response = await apiService.get(`/permissions/user-permissions/${userId}/roles/`);
      return response.data;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des rôles de l\'utilisateur:', error);
      return [];
    }
  }

  /**
   * Récupère les permissions d'un utilisateur
   */
  async getUserPermissionsById(userId: number): Promise<Permission[]> {
    try {
      const response = await apiService.get(`/permissions/user-permissions/${userId}/permissions/`);
      return response.data;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des permissions de l\'utilisateur:', error);
      return [];
    }
  }

  /**
   * Vérifie une permission spécifique côté serveur
   */
  async checkPermission(permissionCode: string): Promise<boolean> {
    try {
      const response = await apiService.get('/permissions/user-permissions/check_permission/', {
        params: { permission: permissionCode }
      });
      return response.data.has_permission;
    } catch (error) {
      console.error('❌ Erreur lors de la vérification de la permission:', error);
      return false;
    }
  }

  /**
   * Réinitialise le cache des permissions
   */
  clearCache(): void {
    this.permissions = [];
    this.roles = [];
    this.permissionCodes = [];
  }

  /**
   * Recharge les permissions de l'utilisateur
   */
  async refreshUserPermissions(): Promise<void> {
    this.clearCache();
    await this.initializeUserPermissions();
  }
}

// Export de l'instance singleton
export const permissionService = new PermissionService();

// Constantes des permissions pour le frontend
export const PERMISSIONS = {
  // Accès général
  ACCESS_APPLICATION: 'access.application',
  
  // Gestion des utilisateurs
  USER_MANAGEMENT_VIEW: 'user_management.view',
  USER_MANAGEMENT_CREATE: 'user_management.create',
  USER_MANAGEMENT_EDIT: 'user_management.edit',
  USER_MANAGEMENT_DELETE: 'user_management.delete',
  USER_MANAGEMENT_ASSIGN_ROLES: 'user_management.assign_roles',
  
  // Configuration système
  SYSTEM_CONFIG_VIEW: 'system_config.view',
  SYSTEM_CONFIG_EDIT: 'system_config.edit',
  SYSTEM_CONFIG_MANAGE_SITES: 'system_config.manage_sites',
  SYSTEM_CONFIG_MANAGE_TENANTS: 'system_config.manage_tenants',
  
  // Pesée
  WEIGHING_VIEW: 'weighing.view',
  WEIGHING_CREATE: 'weighing.create',
  WEIGHING_EDIT: 'weighing.edit',
  WEIGHING_DELETE: 'weighing.delete',
  WEIGHING_CANCEL: 'weighing.cancel',
  WEIGHING_COMPLETE: 'weighing.complete',
  
  // Analyse qualité
  QUALITY_ANALYSIS_VIEW: 'quality_analysis.view',
  QUALITY_ANALYSIS_CREATE: 'quality_analysis.create',
  QUALITY_ANALYSIS_EDIT: 'quality_analysis.edit',
  QUALITY_ANALYSIS_APPROVE: 'quality_analysis.approve',
  QUALITY_ANALYSIS_REJECT: 'quality_analysis.reject',
  
  // Génération de tickets
  TICKET_GENERATION_VIEW: 'ticket_generation.view',
  TICKET_GENERATION_CREATE: 'ticket_generation.create',
  TICKET_GENERATION_PRINT: 'ticket_generation.print',
  TICKET_GENERATION_EMAIL: 'ticket_generation.email',
  
  // Refoulement
  REJECTION_VIEW: 'rejection.view',
  REJECTION_CREATE: 'rejection.create',
  REJECTION_APPROVE: 'rejection.approve',
  
  // Gestion du stock
  STOCK_VIEW: 'stock.view',
  STOCK_CREATE: 'stock.create',
  STOCK_EDIT: 'stock.edit',
  STOCK_DELETE: 'stock.delete',
  STOCK_MANAGE_MOVEMENTS: 'stock.manage_movements',
  STOCK_MANAGE_WAREHOUSES: 'stock.manage_warehouses',
  
  // Statistiques et rapports
  STATISTICS_VIEW: 'statistics.view',
  STATISTICS_EXPORT: 'statistics.export',
  STATISTICS_ADVANCED: 'statistics.advanced',
  
  // Export
  EXPORT_EXCEL: 'export.excel',
  EXPORT_PDF: 'export.pdf',
  EXPORT_CSV: 'export.csv',
  
  // Gestion multi-sites
  MULTI_SITE_VIEW: 'multi_site.view',
  MULTI_SITE_MANAGE: 'multi_site.manage',
  MULTI_SITE_SWITCH: 'multi_site.switch',
  
  // Audit
  AUDIT_VIEW: 'audit.view',
  AUDIT_LOGS: 'audit.logs',
  AUDIT_REPORTS: 'audit.reports',
};

// Constantes des rôles
export const ROLES = {
  ADMIN_SYSTEM: 'admin_system',
  SUPERVISEUR: 'superviseur',
  OPERATEUR_PESEE: 'operateur_pesee',
  CONTROLEUR_QUALITE: 'controleur_qualite',
  STOCK: 'stock',
  COMMERCIAL: 'commercial',
  FINANCE: 'finance',
  TECHNICIEN: 'technicien',
  AUDITEUR: 'auditeur',
};
